import React, { useState, useRef, useEffect } from "react";
import Snackbar from "@mui/material/Snackbar";
import Alert from "@mui/material/Alert";
import "./POffer.css";
import { axiosInstances } from "./apiConfig";

const OrderQuotationEditable = ({ orderData, onPreview, onCancel }) => {
  const [orderNo, setOrderNo] = useState(orderData.orderNo);
  const [orderDate, setOrderDate] = useState(orderData.orderDate);
  const [igst, setIgst] = useState(orderData.igst * 100);
  const [cgst, setCgst] = useState(orderData.cgst * 100);
  const [sgst, setSgst] = useState(orderData.sgst * 100);
  const [authorizedBy, setAuthorizedBy] = useState(orderData.authorizedBy);
  const [offerId, setOfferId] = useState(orderData.offerId || "");
  const [customer, setCustomer] = useState(orderData.customer || {});
  const [items, setItems] = useState(orderData.items || []);
  const [ofrnum, setofrnum] = useState("");
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [ord_sts, setOrd_sts] = useState(orderData.ord_sts || "Open");
  const [subTotal, setSubTotal] = useState(orderData.subTotal);
  const [grandTotal, setGrandTotal] = useState(orderData.grandTotal);
  const [company, setCompany] = useState({
    name: "My Company Pvt Ltd",
    address: "123 Main Street",
    city: "Hyderabad",
    state: "Telangana",
    zip: "500001",
    phone: "040-12345678",
    email: "info@mycompany.com",
  });

  const [terms, setTerms] = useState(
    orderData.terms ||
    `
1. Payment is due within 30 days from the invoice date.
2. Prices are exclusive of applicable taxes unless specified.
3. Delivery timelines are indicative and subject to confirmation at order time.
4. Warranty as per product specifications.
5. Damaged goods must be reported within 7 days.
6. Company reserves the right to modify terms.
7. Returns/replacements follow company policy.
8. Confidentiality must be maintained.
9. Disputes resolved under Hyderabad jurisdiction.
10. Acceptance implies agreement to these terms.
`
  );

  useEffect(() => {
    if (orderData) {
      setOrderNo(orderData.orderNo || "");
      setOrderDate(orderData.orderDate || new Date().toISOString().split("T")[0]);
      setIgst((orderData.igst || 0.18) * 100);
      setCgst((orderData.cgst || 0.09) * 100);
      setSgst((orderData.sgst || 0.09) * 100);
      setAuthorizedBy(orderData.authorizedBy || {});
      setOfferId(orderData.offerId || "");
      setCustomer(orderData.customer || {});
      setItems(orderData.items || []);
      setOrd_sts(orderData.ord_sts || "Open");
      setCompany(orderData.company || {});
      setTerms(orderData.terms || terms);
    }
  }, [orderData]);

  const handleCompanyChange = (e) => {
    setCompany({ ...company, [e.target?.name]: e.target.value });
  };

  const handleCustomerChange = (e) => {
    setCustomer({ ...customer, [e.target?.name]: e.target.value });
  };

  const handleItemChange = (index, field, value) => {
    const newItems = [...items];
    if (field === "quantity" || field === "amount") {
      value = Number(value);
      if (isNaN(value)) value = 0;
    }
    newItems[index][field] = value;
    setItems(newItems);
  };

  const handleTermChange = (e) => setTerms(e.target.value);

  const handleAuthorizedChange = (e) => {
    setAuthorizedBy({ ...authorizedBy, [e.target?.name]: e.target.value });
  };

  const handleSubmit = (e) => {
    e.preventDefault();

    const shippingState =
      customer?.addresses?.find((a) => a.addressType === "Shipping")?.state || "";

    const isTelangana = shippingState.trim().toLowerCase() === "telangana";

    const Billing = customer?.addresses?.find((a) => a.addressType === "Billing") || {};
    const Shipping = customer?.addresses?.find((a) => a.addressType === "Shipping") || {};

    const subTotal = items.reduce(
      (sum, item) => sum + (item.quantity || 0) * (item.amount || 0),
      0
    );

    const normalizeTax = (v) => (v > 1 ? v / 100 : v);

    const newigst = isTelangana ? normalizeTax(igst) : 0;
    const newcgst = isTelangana ? 0 : normalizeTax(cgst);
    const newsgst = isTelangana ? 0 : normalizeTax(sgst);

    const grandTotal = subTotal + subTotal * (newigst + newsgst + newcgst);

    onPreview({
      id: orderData.id || null,
      offerId,
      customerId: customer.id,
      productIds: items.map((item) => item.id),
      company,
      customer: {
        ...customer,
        BillingAddress: Billing,
        ShippingAddress: Shipping,
      },
      orderNo,
      orderDate,
      ord_sts,
      items,
      igst: newigst,
      cgst: newcgst,
      sgst: newsgst,
      terms,
      subTotal,
      grandTotal,
      authorizedBy,
      ord_sts: orderData.ord_sts || "Open",
    });
  };

  const fetchOffer = async () => {
    if (!ofrnum) return;
    try {
      const resp = await axiosInstances.offerApi.post(`/getOffer`, {ofrnum: ofrnum,});
      const data = resp.data.data;

      if (data) {
        const customer = data.customer || {};
        const addresses = customer.addresses || [];
        const Billing = addresses.find((a) => a.addressType === "Billing") || {};
        const Shipping = addresses.find((a) => a.addressType === "Shipping") || {};

        const customerInfo = {
          id: customer.id || "",
          name: customer.name || "",
          email: customer.email || "",
          phone: customer.phone || "",
          addresses: addresses,
          BillingAddress: Billing,
          ShippingAddress: Shipping,
        };

        const orderItems = (data.products || []).map((p) => {
          const priceObj = (data.prices || []).find((price) => price.productId === p.id);
          return {
            id: p.id,
            code: p.id || "",
            priceId: priceObj?.id || "",
            description: p.description || "",
            quantity: priceObj ? priceObj.quantity : 1,
            amount: priceObj ? priceObj.amount : 0,
            total: priceObj ? priceObj.amount : 0,
          };
        });
        setOfferId(data.offer.id)
        setCompany(data.company || company);
        setCustomer(customerInfo);
        setItems(orderItems);
        setIgst(data.offer.igst * 100 || 0);
        setCgst(data.offer.cgst * 100 || 0);
        setSgst(data.offer.sgst * 100 || 0);
        setTerms(data.offer.terms);
      }
    } catch (err) {
      console.error("❌ Error fetching offer:", err);
      setSnackbar({
        open: true,
        message: "Failed to fetch offer data",
        severity: "error",
      });
    }
  };



  return (
    <form onSubmit={handleSubmit} className="container">
      <h1 className="title">Order</h1>

      {/* Offer ID Section */}
      <div style={{ display: "flex", justifyContent: "flex-end", marginBottom: 16 }}>
        <strong>Offer No:&nbsp;</strong>
        {orderData?.id ? (
          <input
            type="text"
            value={ofrnum}
            readOnly
            style={{
              width: "70px",
              marginRight: 20,
              padding: "4px",
              backgroundColor: "#f0f0f0",
              cursor: "not-allowed",
            }}
          />
        ) : (
          <>
            <input
              type="text"
              placeholder="Offer No"
              value={ofrnum}
              onChange={(e) => setofrnum(e.target.value)}
              style={{ width: "120px", padding: "4px" }}
            />
            <button
              type="button"
              onClick={fetchOffer}
              style={{ marginLeft: -1, height: "30px", padding: "4px 10px" }}
            >
              🔍
            </button>

          </>
        )}
      </div>

      <div className="header">
        <div className="company-info">
          <h3>Company Info</h3>
          <label>
            Name:<br />
            <input type="text" name="name" value={company?.name} onChange={handleCompanyChange} className="input" />
          </label>
          <br />
          <label>
            Address:<br />
            <input type="text" name="address" value={company?.address} onChange={handleCompanyChange} className="input" />
          </label>
          <br />
          <label>
            City:<br />
            <input type="text" name="city" value={company?.city} onChange={handleCompanyChange} className="input" />
          </label>
          <br />
          <label>
            State:<br />
            <input type="text" name="state" value={company?.state} onChange={handleCompanyChange} className="input" />
          </label>
          <br />
          <label>
            ZIP:<br />
            <input type="text" name="zip" value={company?.zip} onChange={handleCompanyChange} className="input" />
          </label>
          <br />
          <label>
            Phone:<br />
            <input type="text" name="phone" value={company?.phone} onChange={handleCompanyChange} className="input" />
          </label>
          <br />
          <label>
            Email:<br />
            <input type="email" name="email" value={company?.email} onChange={handleCompanyChange} className="input" />
          </label>
        </div>

        <div className="customer-info">
          <h3>customer Info</h3>
          <label>
            Name:<br />
            <input
              type="text"
              name="name"
              value={customer?.name}
              onChange={handleCustomerChange}
              className="input"
            />
          </label>
          <br />
          <label>
            Email:<br />
            <input
              type="email"
              name="email"
              value={customer?.email || ""}
              onChange={handleCustomerChange}
              className="input"
            />
          </label>
          <br />
          <label>
            Phone:<br />
            <input
              type="text"
              name="phone"
              value={customer?.phone || ""}
              onChange={handleCustomerChange}
              className="input"
            />
          </label>

          {/* --- Billing To --- */}
          <div style={{ marginTop: 20, paddingTop: 10 }}>
            <h4>Billing To:</h4>
            <input
              type="text"
              placeholder="Address Line"
              value={
                customer?.addresses?.find(a => a.addressType === "Billing")?.address_1 || ""
              }
              onChange={(e) => {
                const newAddresses = [...(customer.addresses || [])];
                const BillingIndex = newAddresses.findIndex(a => a.addressType === "Billing");
                if (BillingIndex >= 0)
                  newAddresses[BillingIndex].address_1 = e.target.value;
                else
                  newAddresses.push({
                    addressType: "Billing",
                    address_1: e.target.value,
                    city: "",
                    state: "",
                    pincode: ""
                  });
                setCustomer({ ...customer, addresses: newAddresses });
              }}
              className="input"
            />
            <br />
            <input
              type="text"
              placeholder="City"
              value={
                customer?.addresses?.find(a => a.addressType === "Billing")?.city || ""
              }
              onChange={(e) => {
                const newAddresses = [...(customer.addresses || [])];
                const BillingIndex = newAddresses.findIndex(a => a.addressType === "Billing");
                if (BillingIndex >= 0)
                  newAddresses[BillingIndex].city = e.target.value;
                else
                  newAddresses.push({
                    addressType: "Billing",
                    address_1: "",
                    city: e.target.value,
                    state: "",
                    pincode: ""
                  });
                setCustomer({ ...customer, addresses: newAddresses });
              }}
              className="input"
            />
            <br />
            <input
              type="text"
              placeholder="State"
              value={
                customer?.addresses?.find(a => a.addressType === "Billing")?.state || ""
              }
              onChange={(e) => {
                const newAddresses = [...(customer.addresses || [])];
                const BillingIndex = newAddresses.findIndex(a => a.addressType === "Billing");
                if (BillingIndex >= 0)
                  newAddresses[BillingIndex].state = e.target.value;
                else
                  newAddresses.push({
                    addressType: "Billing",
                    address_1: "",
                    city: "",
                    state: e.target.value,
                    pincode: ""
                  });
                setCustomer({ ...customer, addresses: newAddresses });
              }}
              className="input"
            />
            <br />
            <input
              type="text"
              placeholder="Pincode"
              value={
                customer?.addresses?.find(a => a.addressType === "Billing")?.pincode || ""
              }
              onChange={(e) => {
                const newAddresses = [...(customer.addresses || [])];
                const BillingIndex = newAddresses.findIndex(a => a.addressType === "Billing");
                if (BillingIndex >= 0)
                  newAddresses[BillingIndex].pincode = e.target.value;
                else
                  newAddresses.push({
                    addressType: "Billing",
                    address_1: "",
                    city: "",
                    state: "",
                    pincode: e.target.value
                  });
                setCustomer({ ...customer, addresses: newAddresses });
              }}
              className="input"
            />
          </div>

          {/* --- Ship To --- */}
          <div style={{ marginTop: 20, paddingTop: 10 }}>
            <h4>Ship To:</h4>
            <input
              type="text"
              placeholder="Address Line"
              value={
                customer?.addresses?.find(a => a.addressType === "Shipping")?.address_1 || ""
              }
              onChange={(e) => {
                const newAddresses = [...(customer.addresses || [])];
                const ShippingIndex = newAddresses.findIndex(a => a.addressType === "Shipping");
                if (ShippingIndex >= 0)
                  newAddresses[ShippingIndex].address_1 = e.target.value;
                else
                  newAddresses.push({
                    addressType: "Shipping",
                    address_1: e.target.value,
                    city: "",
                    state: "",
                    pincode: ""
                  });
                setCustomer({ ...customer, addresses: newAddresses });
              }}
              className="input"
            />
            <br />
            <input
              type="text"
              placeholder="City"
              value={
                customer?.addresses?.find(a => a.addressType === "Shipping")?.city || ""
              }
              onChange={(e) => {
                const newAddresses = [...(customer.addresses || [])];
                const ShippingIndex = newAddresses.findIndex(a => a.addressType === "Shipping");
                if (ShippingIndex >= 0)
                  newAddresses[ShippingIndex].city = e.target.value;
                else
                  newAddresses.push({
                    addressType: "Shipping",
                    address_1: "",
                    city: e.target.value,
                    state: "",
                    pincode: ""
                  });
                setCustomer({ ...customer, addresses: newAddresses });
              }}
              className="input"
            />
            <br />
            <input
              type="text"
              placeholder="State"
              value={
                customer?.addresses?.find(a => a.addressType === "Shipping")?.state || ""
              }
              onChange={(e) => {
                const newAddresses = [...(customer.addresses || [])];
                const ShippingIndex = newAddresses.findIndex(a => a.addressType === "Shipping");
                if (ShippingIndex >= 0)
                  newAddresses[ShippingIndex].state = e.target.value;
                else
                  newAddresses.push({
                    addressType: "Shipping",
                    address_1: "",
                    city: "",
                    state: e.target.value,
                    pincode: ""
                  });
                setCustomer({ ...customer, addresses: newAddresses });
              }}
              className="input"
            />
            <br />
            <input
              type="text"
              placeholder="Pincode"
              value={
                customer?.addresses?.find(a => a.addressType === "Shipping")?.pincode || ""
              }
              onChange={(e) => {
                const newAddresses = [...(customer.addresses || [])];
                const ShippingIndex = newAddresses.findIndex(a => a.addressType === "Shipping");
                if (ShippingIndex >= 0)
                  newAddresses[ShippingIndex].pincode = e.target.value;
                else
                  newAddresses.push({
                    addressType: "Shipping",
                    address_1: "",
                    city: "",
                    state: "",
                    pincode: e.target.value
                  });
                setCustomer({ ...customer, addresses: newAddresses });
              }}
              className="input"
            />
          </div>
        </div>
      </div>

      {/* --- Order No & Date --- */}
      <p>
        <label>
          <strong>Order No: </strong>
          <input
            type="text"
            value={orderNo}
            onChange={(e) => setOrderNo(e.target.value)}
            className="input-small"
          />
        </label>
        <br />
        <label>
          <strong>Order Date: </strong>
          <input
            type="date"
            value={orderDate || new Date().toISOString().split("T")[0]}
            onChange={(e) => setOrderDate(e.target.value)}
            className="input-small"
          />
        </label>
        <br />
        <label>
          <strong>Order Status:</strong>
          <select
            value={ord_sts}
            onChange={(e) => setOrd_sts(e.target.value)}
          >
            <option value="DRAFT">DRAFT</option>
            <option value="CONFIRMED">CONFIRMED</option>
            <option value="PROCESSING">PROCESSING</option>
            <option value="SHIPPED">SHIPPED</option>
            <option value="DELIVERED">DELIVERED</option>
          </select>
        </label>
      </p>

      {/* --- Items Table --- */}
      <table className="table">
        <thead>
          <tr>
            <th className="th">Product ID</th>
            <th className="th">Description</th>
            <th className="th">Quantity</th>
            <th className="th">Unit Price (INR)</th>
            <th className="th">Total Price (INR)</th>
          </tr>
        </thead>
        <tbody>
          {(items || []).map((item, i) => (
            <tr key={i}>
              <td className="td">
                <input
                  value={item.code || ""}
                  onChange={(e) => handleItemChange(i, "code", e.target.value)}
                  className="input-table"
                />
              </td>
              <td className="td">
                <textarea
                  value={item.description || ""}
                  onChange={(e) => handleItemChange(i, "description", e.target.value)}
                  className="input-table"
                />
              </td>
              <td className="td">
                <input
                  value={item.quantity || 0}
                  onChange={(e) => handleItemChange(i, "quantity", e.target.value)}
                  className="input-table"
                />
              </td>
              <td className="td">
                <input
                  value={item.amount || 0}
                  onChange={(e) => handleItemChange(i, "amount", e.target.value)}
                  className="input-table"
                />
              </td>
              <td className="td">
                {(item.quantity && item.amount
                  ? item.quantity * item.amount
                  : 0
                ).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      {/* --- Taxes --- */}
      {(customer?.addresses?.find((a) => a.addressType === "Shipping")?.state || "")
        .trim()
        .toLowerCase() === "telangana" ? (
        <div style={{ marginTop: 20, marginBottom: 20 }}>
          <label>
            IGST (%):{" "}
            <input
              value={igst}
              onChange={(e) => setIgst(Number(e.target.value))}
              className="input-small"
            />
          </label>
        </div>
      ) : (
        <>
          <div style={{ marginTop: 20, marginBottom: 10 }}>
            <label>
              CGST (%):{" "}
              <input
                value={cgst}
                onChange={(e) => setCgst(Number(e.target.value))}
                className="input-small"
              />
            </label>
          </div>
          <div style={{ marginBottom: 20 }}>
            <label>
              SGST (%):{" "}
              <input
                value={sgst}
                onChange={(e) => setSgst(Number(e.target.value))}
                className="input-small"
              />
            </label>
          </div>
        </>
      )}

      {/* --- Terms --- */}
      <div style={{ marginBottom: "16px" }}>
        <h3>Terms and Conditions</h3>
        <textarea
          value={terms}
          onChange={handleTermChange}
          className="input"
          rows={6}
          style={{ width: "100%" }}
        />
      </div>

      {/* --- Authorized By --- */}
      <div style={{ marginTop: 40 }}>
        <h3>Authorized By</h3>
        {["name", "position", "company"].map((field) => (
          <label key={field}>
            {field.charAt(0).toUpperCase() + field.slice(1)}:
            <br />
            <input
              type="text"
              name={field}
              value={authorizedBy?.[field] || ""}
              onChange={handleAuthorizedChange}
              className="input"
            />
          </label>
        ))}
      </div>

      {/* --- Buttons --- */}
      <div style={{ marginTop: 30, textAlign: "center" }}>
        <button type="submit" className="save-button">
          Preview Order
        </button>{" "}
        {onCancel && (
          <button type="button" onClick={onCancel} className="cancel-button">
            Cancel
          </button>
        )}
      </div>
    </form>
  );
};

const OrderQuotation = ({ orderData, onEdit, onPrint, onSave }) => {
  const subTotal = orderData.items.reduce(
    (sum, item) => sum + item.quantity * item.amount,
    0
  );

  const grandTotal =
    subTotal + subTotal * (orderData.igst + orderData.cgst + orderData.sgst);

  function convertToInputDateFormat(dateStr) {
    if (!dateStr || !dateStr.includes("-")) return "";
    const [dd, mm, yyyy] = dateStr.split("-");
    return `${yyyy}-${mm}-${dd}`;
  }

  const formatDate = (inputDate) => {
    if (!inputDate) return "";
    const [year, month, day] = inputDate.split("-");
    return `${day}-${month}-${year}`;
  };

  return (
    <div className="container" id="print-section">
      <h1 className="title">Order</h1>

      <div className="header">
        <div className="company-info">
          <strong>{orderData?.company?.name}</strong>
          <br />
          {orderData?.company?.address}
          <br />
          {orderData?.company?.city}
          <br />
          {orderData?.company?.state}
          <br />
          {orderData?.company?.zip}
          <br />
          Phone: {orderData?.company?.phone}
          <br />
          Email: {orderData?.company?.email}
        </div>

        <div className="customer-info">
          <strong>To:</strong>
          <br />
          {orderData.customer?.name}
          <br />
          <strong>Billing Address:</strong>
          <br />
          {orderData.customer?.BillingAddress?.address_1}
          <br />
          {orderData.customer?.BillingAddress?.city}
          <br />
          {orderData.customer?.BillingAddress?.state}
          <br />
          {orderData.customer?.BillingAddress?.pincode}
          <br />
          <br />
          <strong>Shipping Address:</strong>
          <br />
          {orderData.customer?.ShippingAddress?.address_1}
          <br />
          {orderData.customer?.ShippingAddress?.city}
          <br />
          {orderData.customer?.ShippingAddress?.state}
          <br />
          {orderData.customer?.ShippingAddress?.pincode}
        </div>
      </div>

      <p>
        <strong>Order No:</strong> {orderData.orderNo}
        <br />
        <strong>Date:</strong> {formatDate(orderData.orderDate)}
        <br />
        <strong>Status:</strong> {orderData.ord_sts}
      </p>

      <table className="table">
        <thead>
          <tr>
            <th className="th">Product ID</th>
            <th className="th">Description</th>
            <th className="th">Quantity</th>
            <th className="th">Unit Rate (INR)</th>
            <th className="th">Total (INR)</th>
          </tr>
        </thead>
        <tbody>
          {(orderData?.items || []).map((item, i) => (
            <tr key={i}>
              <td className="td">{item.code}</td>
              <td className="td" style={{ padding: 0 }}>
                <div
                  className="input-table"
                  style={{
                    whiteSpace: "pre-wrap",
                    wordWrap: "break-word",
                    padding: 8,
                    minHeight: 34,
                  }}
                >
                  {item.description}
                </div>
              </td>
              <td className="td">{item.quantity}</td>
              <td className="td">
                {item?.amount?.toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>
              <td className="td">
                {(item.quantity * item.amount).toLocaleString(undefined, {
                  minimumFractionDigits: 2,
                  maximumFractionDigits: 2,
                })}
              </td>
            </tr>
          ))}
        </tbody>
      </table>

      <div className="totals">
        <div>
          Subtotal: ₹
          {subTotal.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
        {orderData.igst > 0 ? (
          <div>
            IGST ({(orderData.igst * 100).toFixed(2)}%): ₹
            {(subTotal * orderData.igst).toLocaleString(undefined, {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}
          </div>
        ) : (
          <>
            <div>
              CGST ({(orderData.cgst * 100).toFixed(2)}%): ₹
              {(subTotal * orderData.cgst).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
            <div>
              SGST ({(orderData.sgst * 100).toFixed(2)}%): ₹
              {(subTotal * orderData.sgst).toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })}
            </div>
          </>
        )}
        <div>
          Grand Total: ₹
          {grandTotal.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2,
          })}
        </div>
      </div>

      <div className="terms" style={{ whiteSpace: "pre-wrap" }}>
        <h3>Terms and Conditions</h3>
        {orderData.terms}
      </div>

      <div className="authorized">
        <strong>Authorized By:</strong>
        <br />
        {orderData.authorizedBy?.name}
        <br />
        {orderData.authorizedBy?.position}
        <br />
        {orderData.authorizedBy?.company}
      </div>

      <div style={{ marginTop: 30, textAlign: "center" }} className="no-print">
        <button onClick={onSave} className="save-button">
          {orderData?.id ? "Update" : "Save"}
        </button>{" "}
        <button onClick={onEdit} className="edit-button">
          Edit
        </button>{" "}
        <button onClick={onPrint} className="print-button">
          Print
        </button>
      </div>
    </div>
  );
};

export default function OrderQuotationApp({ orderId, onCancel }) {
  const blankData = {
    enquiryId: "",
    company: {
      name: "My Company Pvt Ltd",
      address: "123 Main Street",
      city: "Hyderabad",
      state: "Telangana",
      zip: "500001",
      phone: "040-12345678",
      email: "info@mycompany.com",
    },
    customer: {
      name: "",
      address: "",
      city: "",
      state: "",
      zip: "",
    },
    orderNo: "",
    orderDate: new Date().toISOString().split("T")[0],
    ord_sts: "Open",
    items: [{ code: "", description: "", quantity: 0, rate: 0 }],
    igst: 0.18,
    cgst: 0.09,
    sgst: 0.09,
    subTotal: 0,
    grandTotal: 0,
    terms: "",
    authorizedBy: { name: "", position: "", company: "" },
  };

  const [orderData, setOrderData] = useState(blankData);
  const [isEditing, setIsEditing] = useState(true);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const handlePreview = (data) => {
    setOrderData(data);
    setIsEditing(false);
  };

  function convertToInputDateFormat(dateStr) {
    if (!dateStr || !dateStr.includes("-")) return "";
    const [dd, mm, yyyy] = dateStr.split("-");
    return `${yyyy}-${mm}-${dd}`;
  }

  const formatDate = (inputDate) => {
    if (!inputDate) return "";
    const [year, month, day] = inputDate.split("-");
    return `${day}-${month}-${year}`;
  };

  useEffect(() => {
    if (!orderId) return; // New order

    axiosInstances.orderApi.get(`/${orderId}`)
      .then((resp) => {
        const data = resp.data.data;
        if (!data) return;

        const order = data.order;
        const customer = data.customer || {};
        const products = data.products || [];
        const prices = data.prices || [];

        const defaultCompany = {
          name: "My Company Pvt Ltd",
          address: "123 Main Street",
          city: "Hyderabad",
          state: "Telangana",
          zip: "500001",
          phone: "040-12345678",
          email: "info@mycompany.com",
        };

        // merge products + prices
        const items = products.map((p) => {
          const price = prices.find((pr) => pr.productId === p.id);
          return {
            id: p.id,
            priceId: price?.id || null,
            code: p.id,
            description: p.description,
            quantity: price?.quantity || 1,
            amount: price?.amount || 0,
          };
        });

        setOrderData((prev) => ({
          ...prev,
          id: order.id,
          enquiryId: order.enquiryId,
          offerId: order.offerId,
          customer: customer,
          company: {
            ...defaultCompany,
            ...(order.company || {}),
          },
          items,
          orderNo: order.orderNo || "",
          orderDate: convertToInputDateFormat(order.orderDate),
          ord_sts: order.ord_sts || "Open",
          igst: order.igst || 0.18,
          cgst: order.cgst || 0.09,
          sgst: order.sgst || 0.09,
          subTotal: order.subTotal || 0,
          grandTotal: order.grandTotal || 0,
          terms: order.terms || "",
          authorizedBy: order.authorizedBy || orderData.authorizedBy,
        }));
        console.log("offerId:", order.offerId);
        console.log("✅ Order loaded successfully", order);
      })
      .catch((err) => {
        console.error("❌ Error loading order:", err);
      });
  }, [orderId]);

  const printRef = useRef();

  const handleSave = async () => {
    try {
      const subTotal = orderData.items.reduce(
        (sum, i) => sum + i.quantity * i.amount,
        0
      );
      const grandTotal =
        subTotal + subTotal * (orderData.igst + orderData.cgst + orderData.sgst);

      const payload = {
        ...orderData,
        id: orderData.id,
        prices: orderData.items.map((i) => ({
          id: i.priceId || null,
          productId: i.id,
          quantity: i.quantity,
          amount: i.amount,
          total: i.amount * i.quantity,
        })),
        orderDate: formatDate(orderData.orderDate),
        subTotal,
        grandTotal,
      };

      let resp;
      if (orderData.id && orderData.id !== 0) {
        resp = await axiosInstances.orderApi.post(`/update`, payload);
      } else {
        resp = await axiosInstances.orderApi.post(`/save`,  payload );
      }

      setOrderData((prev) => ({
        ...prev,
        ...(resp.data.data?.order || resp.data.data || {}),
      }));

      setSnackbar({
        open: true,
        message: orderData.id
          ? "Order updated successfully!"
          : "Order created successfully!",
        severity: "success",
      });

      if (onCancel) {
        setTimeout(() => onCancel(), 1000);
      }
    } catch (err) {
      console.error("❌ Error saving order:", err);
      setSnackbar({
        open: true,
        message: "Failed to save order. Check console for details.",
        severity: "error",
      });
    }
  };

  const handleEdit = () => setIsEditing(true);
  const handlePrint = () => window.print();
  const handleCloseSnackbar = () =>
    setSnackbar({ ...snackbar, open: false });

  return (
    <>
      {isEditing ? (
        <OrderQuotationEditable
          orderData={orderData}
          onPreview={handlePreview}
          onCancel={onCancel}
        />
      ) : (
        <OrderQuotation
          orderData={orderData}
          onEdit={handleEdit}
          onPrint={handlePrint}
          onSave={handleSave}
        />
      )}

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
      >
        <Alert
          onClose={handleCloseSnackbar}
          severity={snackbar.severity}
          sx={{ width: "100%" }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </>
  );
}


